import { useCallback, useEffect, useMemo, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode, readApiMessages } from "@/shared/utils/apiError";
import { downloadBlob } from "@/shared/utils/downloadBlob";
import { colocaToast } from "@/shared/utils/toast";

import {
  AUDITORIA_PAGE_SIZE,
  PAGINATION_SIZES,
  PAYMENT_AUDIT_EXPORT_MAX_ROWS,
} from "../constants/auditoria.constants";
import {
  AuditoriaService,
  buildPaymentAuditFilterParams,
  PAYMENT_AUDIT_EXPORT_TOO_LARGE_CODE,
} from "../services/auditoriaService";
import type { OrganizationAuditGroup } from "../types/auditoria-api.types";
import type { AuditoriaRow, PaymentAuditFiltersState } from "../types/auditoria.types";
import { mapPaymentAuditItemsToRows, mapOrganizationGroupsToRows } from "../utils/auditoriaMapper";

const getDefaultFilters = (): PaymentAuditFiltersState => ({
  organization_name: "",
  client_name: "",
  currency: "",
  amount_min: "",
  amount_max: "",
  start_date: "",
  end_date: "",
  payment_source: "",
});

function getPageNumbers(currentPage: number, lastPage: number): (number | string)[] {
  const maxPagesToShow = 7;
  if (lastPage <= maxPagesToShow) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", lastPage];
  }
  if (currentPage >= lastPage - 2) {
    return [1, "...", lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", lastPage];
}

export interface UsePaymentAuditSectionReturn {
  filters: PaymentAuditFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<PaymentAuditFiltersState>>;
  page: number;
  pageSize: number;
  rows: AuditoriaRow[];
  totalItems: number;
  lastPage: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  from: number;
  to: number;
  isLoading: boolean;
  error: string | null;
  paginationOptions: { label: string; value: string }[];
  pageNumbers: (number | string)[];
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (value: string) => void;
  handleViewHistory: (entityId: string, entityType: string) => void;
  handleCloseHistoryModal: () => void;
  /** Only ever true with rows on screen: there is nothing to put in a report otherwise. */
  canExport: boolean;
  isExporting: boolean;
  handleDownloadExcel: () => void;
  historyModalOpen: boolean;
  historyEntityId: string;
  historyEntityType: string;
  archivosGalleryOpen: boolean;
  selectedArchivosRow: AuditoriaRow | null;
  handleViewArchivos: (row: AuditoriaRow) => void;
  handleCloseArchivosModal: () => void;
  refetch: () => void;
  noAccess: boolean;
}

export function usePaymentAuditSection(): UsePaymentAuditSectionReturn {
  const { t } = useTranslations("audit");
  const [filters, setFilters] = useState<PaymentAuditFiltersState>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<PaymentAuditFiltersState>(getDefaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AUDITORIA_PAGE_SIZE);
  const [rows, setRows] = useState<AuditoriaRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEntityId, setHistoryEntityId] = useState("");
  const [historyEntityType, setHistoryEntityType] = useState("");
  const [archivosGalleryOpen, setArchivosGalleryOpen] = useState(false);
  const [selectedArchivosRow, setSelectedArchivosRow] = useState<AuditoriaRow | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportMessages = useMemo(
    () => ({
      success: t("payments_export_success"),
      error: t("payments_export_error"),
    }),
    [t],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    try {
      const skip = (page - 1) * pageSize;
      const response = await AuditoriaService.fetchPaymentAudit({
        skip,
        limit: pageSize,
        ...buildPaymentAuditFilterParams(appliedFilters),
      });
      const data = response.data;
      const isGrouped = data.data.length > 0 && "logs" in (data.data[0] as OrganizationAuditGroup);
      if (isGrouped) {
        const groups = data.data as OrganizationAuditGroup[];
        setRows(mapOrganizationGroupsToRows(groups));
      } else {
        setRows(mapPaymentAuditItemsToRows(data.data as Parameters<typeof mapPaymentAuditItemsToRows>[0]));
      }
      setTotalItems(data.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
        setRows([]);
        setTotalItems(0);
        return;
      }
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Error al cargar los registros de pagos";
      setError(message);
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    if (!hasAppliedFilters) {
      setRows([]);
      setTotalItems(0);
      setError(null);
      setIsLoading(false);
      return;
    }
    fetchData();
  }, [hasAppliedFilters, fetchData]);

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrevious = page > 1;
  const canGoNext = page < lastPage;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const paginationOptions = useMemo(
    () => PAGINATION_SIZES.map((size) => ({ label: String(size), value: String(size) })),
    [],
  );

  const pageNumbers = useMemo(() => getPageNumbers(page, lastPage), [page, lastPage]);

  const handleApplyFilters = useCallback(() => {
    setHasAppliedFilters(true);
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
    setHasAppliedFilters(false);
    setRows([]);
    setTotalItems(0);
    setError(null);
    setIsLoading(false);
  }, []);

  /**
   * Exports what the table is showing, so it reads `appliedFilters` — the draft in `filters` may have been
   * edited since "Aplicar filtros" and would describe a different slice than the rows on screen.
   */
  const handleDownloadExcel = useCallback(async () => {
    if (rows.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const report = await AuditoriaService.downloadPaymentAuditReport(
        buildPaymentAuditFilterParams(appliedFilters),
      );
      downloadBlob(report.blob, report.filename);
      colocaToast.success(exportMessages.success);
    } catch (err: unknown) {
      // Too many matches is not a failure to report as one: nothing was downloaded, and what the
      // user needs is the count and a narrower filter. Worded here so it follows the dashboard's
      // language and can quote the total the table is already showing.
      if (readApiCode(err) === PAYMENT_AUDIT_EXPORT_TOO_LARGE_CODE) {
        colocaToast.warning(
          t("payments_export_too_large", { total: totalItems, limit: PAYMENT_AUDIT_EXPORT_MAX_ROWS }),
        );
        return;
      }
      const messages = readApiMessages(err);
      colocaToast.error(messages.join(" ") || exportMessages.error);
    } finally {
      setIsExporting(false);
    }
  }, [appliedFilters, rows.length, isExporting, exportMessages, totalItems, t]);

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);

  const handleItemsPerPageChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  const handleViewHistory = useCallback((entityId: string, entityType: string) => {
    setHistoryEntityId(entityId);
    setHistoryEntityType(entityType);
    setHistoryModalOpen(true);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryEntityId("");
    setHistoryEntityType("");
  }, []);

  const handleViewArchivos = useCallback((row: AuditoriaRow) => {
    setSelectedArchivosRow(row);
    setArchivosGalleryOpen(true);
  }, []);

  const handleCloseArchivosModal = useCallback(() => {
    setArchivosGalleryOpen(false);
    setSelectedArchivosRow(null);
  }, []);

  const refetch = useCallback(() => {
    if (hasAppliedFilters) fetchData();
  }, [hasAppliedFilters, fetchData]);

  return {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    handleViewHistory,
    handleCloseHistoryModal,
    canExport: rows.length > 0 && !isLoading && !isExporting,
    isExporting,
    handleDownloadExcel: () => {
      void handleDownloadExcel();
    },
    historyModalOpen,
    historyEntityId,
    historyEntityType,
    archivosGalleryOpen,
    selectedArchivosRow,
    handleViewArchivos,
    handleCloseArchivosModal,
    refetch,
    noAccess,
  };
}
